// ─── SuiviCockpit ────────────────────────────────────────────────────────
// 2026-07-19 — Espace « Suivi » de premier niveau : la boîte de réception
// du matin d'Anissa. Concept validé Benoit (cf. mémoire
// espace_suivi_saas_concept_2026_07_19) :
//
//   1. ENTRANTS d'abord : messages à répondre, puis ressentis/photos info.
//   2. TRAITÉS repliés, avec un état indépendant par événement versionné.
//   3. REPÈRES ensuite : échéances de la semaine et silences.
//
// Règle d'architecture : cet espace ne stocke RIEN — il agrège
// (fetchFeedbacksStream) et agit via les mêmes écritures que la fiche
// (markSuiviEventsTreated, sendCoachMessage). Une seule vérité, deux fenêtres.
// V2 prévue : panneau latéral courbes complètes + échéances (S4/S8,
// renouvellements) + pré-rédaction IA (couplée à consultation, hors V1).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import FeedbacksTrendChart from "./components/FeedbacksTrendChart";
import {
  buildSuiviInbox,
  fetchFeedbacksStream,
  getSuiviEventRefs,
  rollbackOptimisticTreatments,
  stageOptimisticTreatments,
  suiviItemKey,
} from "./services/fetchFeedbacksStream";
import { markSuiviEventsTreated } from "./services/markSuiviEventsTreated";
import { sendCoachMessage } from "./services/sendCoachMessage";
import { getAllClientAlerts } from "./services/clientAlerts";
import { suggestFeedbackReply } from "./services/suggestFeedbackReply";
import { fetchClientMealSummary, mealStateLabel, mealSlotLabel, formatWaterTrend, frNumber } from "./services/fetchClientMealSummary";
import { pushSupportState, isSubscribed, subscribeCoachPush, unsubscribeCoachPush } from "./services/coachPush";
import { analyzeMealPhotoDay } from "./services/analyzeMealPhotoDay";
import { reactMealPhoto } from "./services/reactMealPhoto";
import {
  findUnknownRiskFlags,
  hasRiskProfileAssessment,
  resolveRiskProfileFlags,
} from "./services/riskProfileFlags";
import { getNutritionConsultations } from "./store";

// 2026-07-21 — Réactions emoji sur les photos (façon cockpit Benfitcoach).
// Mêmes emojis, une seule réaction par photo, la cliente la voit dans son app.
const MEAL_REACTIONS = ["❤️", "👍", "💪", "🔥", "👏"];

// ─── Lecture IA des photos (feu vert Anissa 2026-07-22) ─────────────────
// Sortie du mode démo : Anissa a vu la qualité et la prudence de l'analyse
// sur le compte de test et a validé l'extension. Garde-fous actés :
//   1. OPT-IN cliente par cliente : le flag meal_photo_ai_enabled est activé
//      par Anissa dans les réglages app de la fiche (défaut OFF, et le
//      serveur le coupe si les photos sont coupées).
//   2. Uniquement après évaluation canonique du profil par Anissa.
//      Toute fiche absente, incomplète, inconnue ou portant au moins un
//      profil à risque ferme l'action. Le cockpit ne décide jamais lui-même
//      quels risques seraient compatibles avec cette lecture.
// clinique-guard 2026-07-22 (2 correctifs de la revue d'extension) :
//   - le verrou passe par le registre canonique : flag inconnu en DB
//     (id corrompu/renommé) = fail-closed, jamais de feu vert silencieux ;
//   - il agrège TOUTES les fiches locales matchant l'identité (les fiches
//     jumelles par email peuvent diverger — si UNE porte un flag bloquant,
//     on ferme).
function isPhotoAiAllowed(client, locals) {
  const list = Array.isArray(locals) ? locals : locals ? [locals] : [];
  if (client?.meal_photo_ai_enabled !== true) return false;
  // Fail-closed : sans fiche locale, impossible de vérifier les flags → pas d'IA.
  if (list.length === 0) return false;
  for (const loc of list) {
    const form = loc?.form || null;
    if (!form) return false;
    if (!hasRiskProfileAssessment(form)) return false;
    if (findUnknownRiskFlags(form).length > 0) return false;
    const flags = resolveRiskProfileFlags(form);
    if (flags.length > 0) return false;
  }
  return true;
}

const SILENCE_DAYS = 7;
// 2026-07-22 (V2 panneau latéral) : 14 → 30 jours, pour des courbes pleines
// dans le panneau (FeedbacksTrendChart travaille sur une fenêtre de 30 j).
const STREAM_DAYS = 30;
const WEEK_AHEAD_DAYS = 7;

// ─── Intention Suivi → fiche — 2026-07-19 (V2) ──────────────────────────
// « Préparer le rendez-vous » depuis le flux n'ouvre pas juste la fiche :
// il pose une intention (sessionStorage, fraîcheur 60 s, consommée une
// fois par StepFollowup) et la fiche s'ouvre avec la bonne fenêtre déjà
// ouverte. Aucune logique dupliquée : la fiche reste la seule vérité.
export const SUIVI_INTENT_KEY = "anissa_suivi_intent";

function setSuiviIntent(localClientId, action) {
  try {
    sessionStorage.setItem(
      SUIVI_INTENT_KEY,
      JSON.stringify({ localClientId, action, at: Date.now() }),
    );
  } catch {
    /* storage indispo → la fiche s'ouvre sans pré-ouverture, pas bloquant */
  }
}

/** Format futur « jeudi 24 juillet · 14:00 » (aujourd'hui/demain en clair). */
function formatRdv(iso) {
  try {
    const d = new Date(iso);
    const today = new Date();
    const tomorrow = new Date(today.getTime() + 86400000);
    const time = d.getHours() === 0 && d.getMinutes() === 0
      ? null
      : d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    let day;
    if (d.toDateString() === today.toDateString()) day = "Aujourd'hui";
    else if (d.toDateString() === tomorrow.toDateString()) day = "Demain";
    else day = d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
    return time ? `${day} · ${time}` : day;
  } catch {
    return iso;
  }
}

// Libellés factuels des valeurs brutes reçues depuis l'app cliente.
const AXIS_CHIPS = {
  energie: { title: "Énergie", values: { low: "Basse", ok: "OK", good: "Bonne" } },
  fatigue: { title: "Fatigue", values: { better: "Mieux", same: "Pareil", worse: "Pire" } },
  digestion: { title: "Digestion", values: { better: "Mieux", same: "Pareil", worse: "Pire" } },
  faim: { title: "Faim", values: { low: "Faible", ok: "OK", high: "Élevée" } },
};

// Libellés FR des créneaux photo (valeurs DB côté app cliente).
const PHOTO_SLOT_LABELS = {
  petit_dejeuner: "Petit-déjeuner",
  dejeuner: "Déjeuner",
  diner: "Dîner",
  collation: "Collation",
};

const FACT_CHIP_STYLE = { color: "rgba(245,242,233,.72)", background: "rgba(255,255,255,.08)" };

function daysSince(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return null;
  return Math.floor(ms / 86400000);
}

// Boucle de suivi 2026-07-25 — jour court pour le détail par journée.
function formatDayShort(dayIso) {
  try {
    const d = new Date(dayIso + "T12:00:00Z");
    const today = new Date().toISOString().slice(0, 10);
    if (dayIso === today) return "Aujourd'hui";
    return d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
  } catch {
    return dayIso;
  }
}

function formatWhen(iso) {
  try {
    const d = new Date(iso);
    const today = new Date();
    const isToday = d.toDateString() === today.toDateString();
    const yesterday = new Date(today.getTime() - 86400000);
    const isYesterday = d.toDateString() === yesterday.toDateString();
    const time = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    if (isToday) return `Aujourd'hui · ${time}`;
    if (isYesterday) return `Hier · ${time}`;
    return `${d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })} · ${time}`;
  } catch {
    return iso;
  }
}

export default function SuiviCockpit({ sharedClients = [], ownClients = [], onOpenClient }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stream, setStream] = useState({ clients: [], feedbacks: [], photos: [], clientMessages: [] });
  // Traitement optimiste par référence versionnée. La valeur est l'id de
  // l'opération qui l'a posée, afin qu'un rollback tardif n'annule jamais
  // une opération plus récente sur la même référence.
  const [optimisticTreatments, setOptimisticTreatments] = useState({});
  const treatmentSequence = useRef(0);
  const [treatmentStatus, setTreatmentStatus] = useState(null);
  const [showTreated, setShowTreated] = useState(false);
  const [chartFor, setChartFor] = useState(null); // feedback.id dont la courbe est dépliée
  const [replyFor, setReplyFor] = useState(null); // feedback.id dont la réponse est ouverte
  const [replyText, setReplyText] = useState("");
  const [replyBusy, setReplyBusy] = useState(false);
  const [replyStatus, setReplyStatus] = useState(null); // { id, ok, msg }
  // Visionneuse photos (2026-07-21, retour Benoit : plus de nouvel onglet).
  // { photos: [photos avec url], index, clientName } | null
  const [lightbox, setLightbox] = useState(null);
  // Push Anissa (2026-07-26) : état du bouton notifications de CET appareil.
  // hidden = push non supporté (bouton absent) | denied = permission refusée
  // dans le navigateur | off/on = abonnement de l'appareil | busy = en cours.
  const [pushState, setPushState] = useState("hidden");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const support = pushSupportState();
      if (support === "unsupported") return;
      if (support === "denied") {
        if (!cancelled) setPushState("denied");
        return;
      }
      const subbed = await isSubscribed();
      if (!cancelled) setPushState(subbed ? "on" : "off");
    })();
    return () => { cancelled = true; };
  }, []);

  const handlePushToggle = useCallback(async () => {
    setPushState((prev) => {
      if (prev === "busy" || prev === "denied" || prev === "hidden") return prev;
      (async () => {
        try {
          if (prev === "on") {
            await unsubscribeCoachPush();
            setPushState("off");
          } else {
            await subscribeCoachPush();
            setPushState("on");
          }
        } catch (e) {
          setPushState(e?.message === "permission_refusee" ? "denied" : prev);
        }
      })();
      return "busy";
    });
  }, []);
  // Pré-rédaction IA de la réponse (2026-07-21) : remplit la zone de texte,
  // Anissa relit/ajuste, JAMAIS d'envoi automatique.
  const [aiBusy, setAiBusy] = useState(false);
  // Garde anti-course (relevée par clinique-guard) : si Anissa change de
  // carte pendant la rédaction, le brouillon de la cliente A ne doit JAMAIS
  // atterrir dans la zone de réponse de la cliente B. On mémorise la cible
  // et on jette le résultat si la boîte ouverte a changé entre-temps.
  const replyForRef = useRef(null);
  replyForRef.current = replyFor;
  // clinique-guard 2026-07-21 : miroir du texte en cours (même pattern).
  // Les closures capturées avant un await IA (plusieurs secondes) sont
  // périmées — la garde « ne jamais écraser une saisie » doit lire les refs.
  const replyTextRef = useRef("");
  replyTextRef.current = replyText;

  // 2026-07-21 — Lecture IA des photos (mode démo Chloé Test). La lecture
  // reste privée (encadré dédié, jamais envoyée) ; le brouillon remplit la
  // zone de réponse SAUF si Anissa est déjà en train d'écrire à quelqu'un
  // d'autre (on ne vole jamais une saisie en cours).
  const [photoAi, setPhotoAi] = useState(null); // { gid, status: 'busy'|'done'|'error', lecture?, msg? }

  // 2026-07-22 — V2 : panneau latéral par cliente (clic sur son prénom).
  // Courbes 30 j, échéances, dernière note — sans quitter le flux.
  const [panelFor, setPanelFor] = useState(null); // client_id | null
  useEffect(() => {
    if (!panelFor) return;
    const onKey = (e) => {
      if (e.key === "Escape") setPanelFor(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [panelFor]);

  // Boucle de suivi 2026-07-25 — synthèse repas + hydratation 7 jours,
  // chargée à l'ouverture du panneau. Des faits, jamais de score.
  const [mealSummary, setMealSummary] = useState(null); // { status, meals?, water? }
  useEffect(() => {
    if (!panelFor) {
      setMealSummary(null);
      return;
    }
    const client = clientById.get(panelFor);
    if (!client) return;
    let dead = false;
    setMealSummary({ status: "busy" });
    fetchClientMealSummary(client, 7).then((res) => {
      if (dead) return;
      if (res.ok && res.found) {
        setMealSummary({ status: "done", meals: res.meals, water: res.water });
      } else {
        setMealSummary({ status: "empty" });
      }
    });
    return () => {
      dead = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelFor]);

  async function handlePhotoAiAnalysis(client, local, group, gid) {
    // clinique-guard 2026-07-21 : défense en profondeur — le garde-fou est
    // re-vérifié ici (opt-in + TCA sur toutes les fiches), pas seulement
    // sur l'affichage du bouton.
    if (!isPhotoAiAllowed(client, findLocalAll(client))) return;
    if (photoAi?.status === "busy") return;
    setPhotoAi({ gid, status: "busy" });
    try {
      const planText = local ? (getNutritionConsultations(local.id)[0]?.nutritionPlan || "") : "";
      const photos = group.photos
        .filter((p) => p.url)
        .map((p) => ({ url: p.url, slotLabel: PHOTO_SLOT_LABELS[p.slot] || "Repas", note: p.note || "" }));
      const { lecture, brouillon } = await analyzeMealPhotoDay({
        clientFirstName: client.first_name,
        photos,
        planText,
      });
      setPhotoAi({ gid, status: "done", lecture });
      // Lu sur les refs APRÈS l'await (clinique-guard) : si Anissa a ouvert
      // ou rempli la réponse d'une autre cliente pendant l'analyse, on ne
      // touche à rien — la lecture privée s'affiche, le brouillon est perdu.
      const saisieAilleurs =
        (replyForRef.current !== null && replyForRef.current !== gid) ||
        (replyTextRef.current.trim().length > 0 && replyForRef.current !== gid);
      if (!saisieAilleurs) {
        setReplyFor(gid);
        replyForRef.current = gid;
        setReplyText(brouillon);
        setReplyStatus(null);
      }
    } catch (e) {
      setPhotoAi({ gid, status: "error", msg: e?.message || "Analyse impossible — réessaie" });
    }
  }

  // 2026-07-21 — Réaction emoji sur une photo, optimiste. Toggle : re-cliquer
  // le même emoji l'enlève. Met à jour le flux ET la visionneuse ouverte ;
  // revert silencieux si l'API échoue (une réaction est un geste léger).
  function applyPhotoReaction(photoId, reaction) {
    setStream((s) => ({
      ...s,
      photos: s.photos.map((p) => (p.id === photoId ? { ...p, reaction } : p)),
    }));
    setLightbox((lb) =>
      lb ? { ...lb, photos: lb.photos.map((p) => (p.id === photoId ? { ...p, reaction } : p)) } : lb,
    );
  }

  async function handleReactPhoto(photo, emoji) {
    const next = photo.reaction === emoji ? null : emoji;
    const prev = photo.reaction ?? null;
    applyPhotoReaction(photo.id, next);
    try {
      await reactMealPhoto({ photoId: photo.id, reaction: next });
    } catch {
      applyPhotoReaction(photo.id, prev);
    }
  }

  async function handleAiDraft(client, context, id) {
    if (aiBusy) return;
    setAiBusy(true);
    setReplyStatus(null);
    try {
      // 2026-07-21 (validation Anissa) : profil sensible = sans-chiffres,
      // flag TCA, ou fiche locale introuvable (fail-closed). Pour ces
      // profils l'IA n'émet aucun compliment ni jugement sur les aliments
      // photographiés — observations neutres uniquement.
      const localForm = findLocal(client)?.form || null;
      const riskFlags = Array.isArray(localForm?.flagsProfilRisque) ? localForm.flagsProfilRisque : [];
      const profilSensible =
        !localForm ||
        localForm.presentationSansChiffres === true ||
        riskFlags.includes("tca");
      const draft = await suggestFeedbackReply({
        clientFirstName: client.first_name,
        context,
        profilSensible,
      });
      if (replyForRef.current !== id) return; // la cible a changé → on jette
      setReplyText(draft);
    } catch (e) {
      if (replyForRef.current === id) {
        setReplyStatus({ id, ok: false, msg: e?.message || "Pré-rédaction impossible — réessaie" });
      }
    } finally {
      setAiBusy(false);
    }
  }

  // Navigation clavier de la visionneuse : Échap ferme, ← → naviguent.
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      else if (e.key === "ArrowRight")
        setLightbox((lb) => lb && { ...lb, index: (lb.index + 1) % lb.photos.length });
      else if (e.key === "ArrowLeft")
        setLightbox((lb) => lb && { ...lb, index: (lb.index - 1 + lb.photos.length) % lb.photos.length });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchFeedbacksStream(STREAM_DAYS);
      setStream(data);
    } catch (e) {
      setError(e?.message || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Fiches SaaS locales, pour le lien « Ouvrir la fiche » (match par email
  // ou par stagingClientId — hide-my-email Apple oblige).
  const localByKey = useMemo(() => {
    const map = new Map();
    for (const c of [...sharedClients, ...ownClients]) {
      const email = (c.form?.email || c.email || "").trim().toLowerCase();
      if (email) map.set(`email:${email}`, c);
      if (c.stagingClientId) map.set(`id:${c.stagingClientId}`, c);
    }
    return map;
  }, [sharedClients, ownClients]);

  const findLocal = useCallback(
    (streamClient) => {
      if (!streamClient) return null;
      return (
        localByKey.get(`id:${streamClient.client_id}`) ||
        localByKey.get(`email:${(streamClient.email || "").trim().toLowerCase()}`) ||
        null
      );
    },
    [localByKey],
  );

  // clinique-guard 2026-07-22 : pour le verrou TCA de la lecture IA, on
  // récupère TOUTES les fiches locales matchant l'identité (id staging OU
  // email) — pas seulement la « gagnante » de localByKey, que des fiches
  // jumelles divergentes pourraient contourner.
  const findLocalAll = useCallback(
    (streamClient) => {
      if (!streamClient) return [];
      const email = (streamClient.email || "").trim().toLowerCase();
      return [...sharedClients, ...ownClients].filter((c) => {
        if (c.stagingClientId && c.stagingClientId === streamClient.client_id) return true;
        const cEmail = (c.form?.email || c.email || "").trim().toLowerCase();
        return !!email && cEmail === email;
      });
    },
    [sharedClients, ownClients],
  );

  const clientById = useMemo(() => {
    const map = new Map();
    for (const c of stream.clients) map.set(c.client_id, c);
    return map;
  }, [stream.clients]);

  const feedbacksByClient = useMemo(() => {
    const map = new Map();
    for (const f of stream.feedbacks) {
      const list = map.get(f.client_id) || [];
      list.push(f);
      map.set(f.client_id, list);
    }
    return map;
  }, [stream.feedbacks]);

  // ── Cette semaine : RDV posés à venir + bilans S4/S8/S12 dus ─────────
  // RDV : rdv_scheduled_at du stream (source unique = la fiche, qui le
  // synchronise vers l'app). Bilans : clientAlerts (pack steps uniquement
  // depuis le nettoyage du 2026-07-19).
  const upcomingRdv = useMemo(() => {
    const now = Date.now();
    const horizon = now + WEEK_AHEAD_DAYS * 86400000;
    return stream.clients
      .filter((c) => {
        if (!c.rdv_scheduled_at) return false;
        const t = new Date(c.rdv_scheduled_at).getTime();
        // Garde les RDV du jour même passés de quelques heures (suivi en cours)
        return Number.isFinite(t) && t > now - 12 * 3600000 && t < horizon;
      })
      .sort((a, b) => new Date(a.rdv_scheduled_at) - new Date(b.rdv_scheduled_at));
  }, [stream.clients]);

  const packAlerts = (() => {
    try {
      return getAllClientAlerts(6);
    } catch {
      return [];
    }
  })();

  // ── Silences : programme actif, suivi (pas oneshot), rien depuis 7 j ──
  const silences = useMemo(() => {
    return stream.clients
      .filter((c) => c.mode !== "oneshot" && c.journey_status === "program_active")
      .map((c) => {
        const fbs = feedbacksByClient.get(c.client_id) || [];
        const last = fbs.length > 0 ? fbs[0].created_at : null;
        const days = last ? daysSince(last) : null;
        return { client: c, lastFeedbackAt: last, daysSilent: days };
      })
      .filter((s) => s.daysSilent === null || s.daysSilent >= SILENCE_DAYS)
      .sort((a, b) => (b.daysSilent ?? 99) - (a.daysSilent ?? 99));
  }, [stream.clients, feedbacksByClient]);

  // ── Flux opérationnel : messages à répondre, informations, traités ──
  const optimisticKeys = useMemo(
    () => new Set(Object.keys(optimisticTreatments)),
    [optimisticTreatments],
  );
  const { toReply, information, treated } = useMemo(
    () => buildSuiviInbox(stream, optimisticKeys),
    [stream, optimisticKeys],
  );

  // ── Actions ──────────────────────────────────────────────────────────
  async function handleTreat(item) {
    const refs = getSuiviEventRefs(item);
    const itemId = suiviItemKey(item);
    const operationId = ++treatmentSequence.current;
    setTreatmentStatus(null);
    setOptimisticTreatments((prev) => stageOptimisticTreatments(prev, refs, operationId));
    try {
      await markSuiviEventsTreated(refs);
      return true;
    } catch (e) {
      setOptimisticTreatments((prev) => rollbackOptimisticTreatments(prev, refs, operationId));
      setTreatmentStatus({ id: itemId, msg: e?.message || "Traitement impossible — réessaie" });
      return false;
    }
  }

  async function handleSendReply(item) {
    const client = item.client;
    const itemId = suiviItemKey(item);
    const body = replyText.trim();
    if (!body || replyBusy) return;
    setReplyBusy(true);
    setReplyStatus(null);
    try {
      await sendCoachMessage({ email: client.email, body, source: "manual" });
      const marked = await handleTreat(item);
      setReplyStatus({
        id: itemId,
        ok: marked,
        msg: marked ? "Envoyé à la cliente ✓" : "Message envoyé, mais le traitement n'a pas été enregistré",
      });
      setReplyText("");
      setReplyFor(null);
    } catch (e) {
      setReplyStatus({ id: itemId, ok: false, msg: e?.message || "Échec de l'envoi" });
    } finally {
      setReplyBusy(false);
    }
  }

  // ── Rendu d'un item du flux (ressenti ou groupe de photos) ───────────
  function renderItem(item, { isTreated }) {
    if (item.type === "photos") return renderPhotoGroup(item, { isTreated });
    if (item.type === "message") return renderMessageCard(item, { isTreated });
    return renderCard(item, { isTreated });
  }

  // ── Carte « message de la cliente » (2026-07-22, messagerie cliente →
  // Anissa). « Répondre » est l'action principale ; la réponse envoyée
  // devient un message classique et traite seulement cette carte.
  function renderMessageCard(item, { isTreated }) {
    const { message: m, client } = item;
    const gid = suiviItemKey(item);
    const local = findLocal(client);
    return (
      <article key={gid} className="suivi-inbox-card" style={{ ...cardStyle, opacity: isTreated ? 0.72 : 1 }}>
        <header style={cardHeaderStyle}>
          <div style={{ minWidth: 0 }}>
            <div style={cardNameRowStyle}>
              <button type="button" style={cardNameBtnStyle} title="Courbes et échéances" onClick={() => setPanelFor(client.client_id)}>{client.first_name || "Cliente"}</button>
              <span style={{ ...badgeStyle, color: "#c9a96a", background: "rgba(201,169,106,0.16)" }}>
                Message
              </span>
              {!isTreated && <span style={newBadgeStyle}>Nouveau</span>}
            </div>
            <div style={cardWhenStyle}>{formatWhen(m.sent_at)}</div>
          </div>
        </header>

        <p style={noteStyle}>« {m.body} »</p>

        <div className="suivi-inbox-actions" style={actionsRowStyle}>
          <button
            type="button"
            style={seenBtnStyle}
            onClick={() => {
              setReplyFor(replyFor === gid ? null : gid);
              setReplyStatus(null);
            }}
          >
            Répondre
          </button>
          {!isTreated && (
            <button type="button" style={ghostBtnStyle} onClick={() => handleTreat(item)}>
              Marquer comme traité
            </button>
          )}
          {local && onOpenClient && (
            <button type="button" style={ghostBtnStyle} onClick={() => onOpenClient(local.id)}>
              Ouvrir la fiche
            </button>
          )}
        </div>

        {treatmentStatus?.id === gid && (
          <p style={{ ...replyStatusStyle, color: "#e89090" }}>{treatmentStatus.msg}</p>
        )}

        {replyStatus?.id === gid && (
          <p style={{ ...replyStatusStyle, color: replyStatus.ok ? "#8fd4a8" : "#e89090" }}>{replyStatus.msg}</p>
        )}

        {replyFor === gid && (
          <div style={replyBoxStyle}>
            <textarea
              style={replyTextareaStyle}
              rows={3}
              maxLength={2000}
              placeholder={`Ta réponse à ${client.first_name || "la cliente"}… (message dans son app)`}
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              autoFocus
            />
            <div className="suivi-inbox-reply-actions" style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                type="button"
                style={{ ...ghostBtnStyle, marginRight: "auto" }}
                disabled={aiBusy}
                onClick={() =>
                  handleAiDraft(
                    client,
                    `Message écrit par la cliente dans son app (${formatWhen(m.sent_at)}) : « ${String(m.body).slice(0, 600)} »`,
                    gid,
                  )
                }
              >
                {aiBusy ? "Préparation…" : "Préparer une réponse"}
              </button>
              <button type="button" style={ghostBtnStyle} onClick={() => { setReplyFor(null); setReplyText(""); }}>
                Annuler
              </button>
              <button
                type="button"
                style={{ ...seenBtnStyle, opacity: replyText.trim() && !replyBusy ? 1 : 0.5 }}
                disabled={!replyText.trim() || replyBusy}
                onClick={() => handleSendReply(item)}
              >
                {replyBusy ? "Envoi…" : "Envoyer"}
              </button>
            </div>
          </div>
        )}
      </article>
    );
  }

  // ── Carte « photos de repas » (2026-07-21) — groupée cliente × jour ──
  function renderPhotoGroup(item, { isTreated }) {
    const { group, client } = item;
    const gid = suiviItemKey(item);
    const local = findLocal(client);
    const noted = group.photos.filter((p) => p.note);
    return (
      <article key={gid} className="suivi-inbox-card" style={{ ...cardStyle, opacity: isTreated ? 0.72 : 1 }}>
        <header style={cardHeaderStyle}>
          <div style={{ minWidth: 0 }}>
            <div style={cardNameRowStyle}>
              <button type="button" style={cardNameBtnStyle} title="Courbes et échéances" onClick={() => setPanelFor(client.client_id)}>{client.first_name || "Cliente"}</button>
              <span style={{ ...badgeStyle, color: "#8fd4a8", background: "rgba(130,195,158,0.18)" }}>
                {group.photos.length} photo{group.photos.length > 1 ? "s" : ""} de repas
              </span>
              {!isTreated && <span style={newBadgeStyle}>Nouveau</span>}
            </div>
            <div style={cardWhenStyle}>{formatWhen(group.photos[group.photos.length - 1].created_at)}</div>
          </div>
        </header>

        {/* Vignettes — clic : visionneuse intégrée (croix, flèches, clavier) */}
        <div style={photoRowStyle}>
          {group.photos.map((p) =>
            p.url ? (
              <button
                key={p.id}
                type="button"
                title={`${PHOTO_SLOT_LABELS[p.slot] || "Repas"}${p.note ? ` — ${p.note}` : ""}`}
                style={photoThumbWrapStyle}
                onClick={() => {
                  const withUrl = group.photos.filter((x) => x.url);
                  const index = Math.max(0, withUrl.findIndex((x) => x.id === p.id));
                  setLightbox({ photos: withUrl, index, clientName: client.first_name || "Cliente" });
                }}
              >
                <span style={{ position: "relative", display: "block" }}>
                  <img src={p.url} alt={PHOTO_SLOT_LABELS[p.slot] || "Photo de repas"} style={photoThumbStyle} />
                  {p.reaction ? <span style={photoThumbReactBadgeStyle}>{p.reaction}</span> : null}
                </span>
                <span style={photoThumbLabelStyle}>{PHOTO_SLOT_LABELS[p.slot] || "Repas"}</span>
              </button>
            ) : null,
          )}
        </div>

        {noted.length > 0 && (
          <div style={{ marginTop: 8 }}>
            {noted.map((p) => (
              <p key={`note-${p.id}`} style={noteStyle}>
                {PHOTO_SLOT_LABELS[p.slot] || "Repas"} · « {p.note} »
              </p>
            ))}
          </div>
        )}

        <div className="suivi-inbox-actions" style={actionsRowStyle}>
          {!isTreated && (
            <button type="button" style={seenBtnStyle} onClick={() => handleTreat(item)}>
              Marquer comme traité
            </button>
          )}
          <button
            type="button"
            style={ghostBtnStyle}
            onClick={() => {
              setReplyFor(replyFor === gid ? null : gid);
              setReplyStatus(null);
            }}
          >
            Répondre
          </button>
          {/* Opt-in par cliente + verrou TCA (feu vert Anissa 2026-07-22) */}
          {isPhotoAiAllowed(client, findLocalAll(client)) && (
            <button
              type="button"
              style={{ ...ghostBtnStyle, borderColor: "rgba(201,169,106,.4)", color: "#c9a96a" }}
              disabled={photoAi?.status === "busy"}
              onClick={() => handlePhotoAiAnalysis(client, local, group, gid)}
            >
              {photoAi?.status === "busy" && photoAi?.gid === gid ? "Préparation…" : "Préparer un retour"}
            </button>
          )}
          {local && onOpenClient && (
            <button type="button" style={ghostBtnStyle} onClick={() => onOpenClient(local.id)}>
              Ouvrir la fiche
            </button>
          )}
        </div>

        {treatmentStatus?.id === gid && (
          <p style={{ ...replyStatusStyle, color: "#e89090" }}>{treatmentStatus.msg}</p>
        )}

        {photoAi?.gid === gid && photoAi.status === "done" && (
          <div style={aiLectureBoxStyle}>
            <div style={aiLectureTitleStyle}>Lecture privée — visible par toi seule, jamais envoyée</div>
            <p style={aiLectureTextStyle}>{photoAi.lecture}</p>
          </div>
        )}
        {photoAi?.gid === gid && photoAi.status === "error" && (
          <p style={{ ...replyStatusStyle, color: "#e89090" }}>{photoAi.msg}</p>
        )}

        {replyStatus?.id === gid && (
          <p style={{ ...replyStatusStyle, color: replyStatus.ok ? "#8fd4a8" : "#e89090" }}>{replyStatus.msg}</p>
        )}

        {replyFor === gid && (
          <div style={replyBoxStyle}>
            <textarea
              style={replyTextareaStyle}
              rows={3}
              maxLength={2000}
              placeholder={`Un retour à ${client.first_name || "la cliente"} sur ses repas… (message dans son app)`}
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              autoFocus
            />
            <div className="suivi-inbox-reply-actions" style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              {/* Pré-rédaction IA : brouillon dans la voix d'Anissa, périmètre
                  relationnel strict (cf. suggestFeedbackReply). Elle relit. */}
              <button
                type="button"
                style={{ ...ghostBtnStyle, marginRight: "auto" }}
                disabled={aiBusy}
                onClick={() =>
                  handleAiDraft(
                    client,
                    `${group.photos.length} photo(s) de repas (${formatWhen(group.photos[group.photos.length - 1].created_at)}) : ${group.photos
                      .map((p) => {
                        const label = PHOTO_SLOT_LABELS[p.slot] || "Repas";
                        return p.note ? `${label} (mot de la cliente : « ${p.note} »)` : label;
                      })
                      .join(", ")}.`,
                    gid,
                  )
                }
              >
                {aiBusy ? "Préparation…" : "Préparer un retour"}
              </button>
              <button type="button" style={ghostBtnStyle} onClick={() => { setReplyFor(null); setReplyText(""); }}>
                Annuler
              </button>
              <button
                type="button"
                style={{ ...seenBtnStyle, opacity: replyText.trim() && !replyBusy ? 1 : 0.5 }}
                disabled={!replyText.trim() || replyBusy}
                onClick={() => handleSendReply(item)}
              >
                {replyBusy ? "Envoi…" : "Envoyer"}
              </button>
            </div>
          </div>
        )}
      </article>
    );
  }

  // ── Rendu d'une carte ressenti ───────────────────────────────────────
  function renderCard(item, { isTreated }) {
    const { feedback: f, client } = item;
    const itemId = suiviItemKey(item);
    const clientFbs = feedbacksByClient.get(client.client_id) || [];
    const local = findLocal(client);
    const chips = Object.entries(AXIS_CHIPS)
      .map(([axis, meta]) => {
        const raw = f[axis];
        const v = raw && meta.values[raw];
        return v ? { title: meta.title, label: v } : null;
      })
      .filter(Boolean);

    return (
      <article key={itemId} className="suivi-inbox-card" style={{ ...cardStyle, opacity: isTreated ? 0.72 : 1 }}>
        <header style={cardHeaderStyle}>
          <div style={{ minWidth: 0 }}>
            <div style={cardNameRowStyle}>
              <button type="button" style={cardNameBtnStyle} title="Courbes et échéances" onClick={() => setPanelFor(client.client_id)}>{client.first_name || "Cliente"}</button>
              <span style={{ ...badgeStyle, color: "#c9a96a", background: "rgba(201,169,106,0.16)" }}>Ressenti</span>
              {!isTreated && <span style={newBadgeStyle}>Nouveau</span>}
            </div>
            <div style={cardWhenStyle}>{formatWhen(f.created_at)}</div>
          </div>
        </header>

        {chips.length > 0 && (
          <div style={chipsRowStyle}>
            {chips.map((c) => (
              <span key={c.title} style={{ ...chipStyle, ...FACT_CHIP_STYLE }}>
                {c.title} : {c.label}
              </span>
            ))}
            {typeof f.weight_kg === "number" && (
              <span style={{ ...chipStyle, ...FACT_CHIP_STYLE }}>
                Poids : {f.weight_kg} kg
              </span>
            )}
          </div>
        )}

        {f.note && <p style={noteStyle}>« {f.note} »</p>}

        <div className="suivi-inbox-actions" style={actionsRowStyle}>
          {!isTreated && (
            <button type="button" style={seenBtnStyle} onClick={() => handleTreat(item)}>
              Marquer comme traité
            </button>
          )}
          <button
            type="button"
            style={ghostBtnStyle}
            onClick={() => {
              setReplyFor(replyFor === f.id ? null : f.id);
              setReplyStatus(null);
            }}
          >
            Répondre
          </button>
          {/* FeedbacksTrendChart ne rend rien sous 3 points : on n'offre le
              bouton que quand la courbe a de la matière. */}
          {clientFbs.length >= 3 && (
            <button
              type="button"
              style={ghostBtnStyle}
              onClick={() => setChartFor(chartFor === f.id ? null : f.id)}
            >
              {chartFor === f.id ? "Masquer la courbe" : "Courbe 14 j"}
            </button>
          )}
          {local && onOpenClient && (
            <button type="button" style={ghostBtnStyle} onClick={() => onOpenClient(local.id)}>
              Ouvrir la fiche
            </button>
          )}
        </div>

        {treatmentStatus?.id === itemId && (
          <p style={{ ...replyStatusStyle, color: "#e89090" }}>{treatmentStatus.msg}</p>
        )}

        {replyStatus?.id === itemId && (
          <p style={{ ...replyStatusStyle, color: replyStatus.ok ? "#8fd4a8" : "#e89090" }}>{replyStatus.msg}</p>
        )}

        {replyFor === f.id && (
          <div style={replyBoxStyle}>
            <textarea
              style={replyTextareaStyle}
              rows={3}
              maxLength={2000}
              placeholder={`Répondre à ${client.first_name || "la cliente"}… (message dans son app)`}
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              autoFocus
            />
            <div className="suivi-inbox-reply-actions" style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              {/* Pré-rédaction IA : brouillon dans la voix d'Anissa, périmètre
                  relationnel strict (cf. suggestFeedbackReply). Elle relit. */}
              <button
                type="button"
                style={{ ...ghostBtnStyle, marginRight: "auto" }}
                disabled={aiBusy}
                onClick={() => {
                  const bits = chips.map((c) => `${c.title} : ${c.label}`);
                  // clinique-guard 2026-07-21 — le poids n'entre dans le
                  // contexte IA QUE si la fiche locale est retrouvée ET que
                  // la cliente n'est ni « sans chiffres » ni flag TCA.
                  // Fiche introuvable → fail-closed : pas de poids.
                  const localForm = local?.form || null;
                  const riskFlags = Array.isArray(localForm?.flagsProfilRisque) ? localForm.flagsProfilRisque : [];
                  const weightAllowed =
                    !!localForm &&
                    localForm.presentationSansChiffres !== true &&
                    !riskFlags.includes("tca");
                  if (weightAllowed && typeof f.weight_kg === "number") {
                    bits.push(`Poids saisi : ${f.weight_kg} kg`);
                  }
                  let ctx = `Ressenti quotidien (${formatWhen(f.created_at)}) — ${bits.join(", ")}.`;
                  if (f.note) ctx += ` Note libre : « ${f.note} »`;
                  handleAiDraft(client, ctx, f.id);
                }}
              >
                {aiBusy ? "Préparation…" : "Préparer une réponse"}
              </button>
              <button type="button" style={ghostBtnStyle} onClick={() => { setReplyFor(null); setReplyText(""); }}>
                Annuler
              </button>
              <button
                type="button"
                style={{ ...seenBtnStyle, opacity: replyText.trim() && !replyBusy ? 1 : 0.5 }}
                disabled={!replyText.trim() || replyBusy}
                onClick={() => handleSendReply(item)}
              >
                {replyBusy ? "Envoi…" : "Envoyer"}
              </button>
            </div>
          </div>
        )}

        {chartFor === f.id && (
          <div style={{ marginTop: 10 }}>
            <FeedbacksTrendChart feedbacks={clientFbs} />
          </div>
        )}
      </article>
    );
  }

  // ── Rendu ────────────────────────────────────────────────────────────
  return (
    <div style={wrapStyle}>
      <header style={pageHeaderStyle}>
        <div>
          <p style={eyebrowStyle}>Suivi · toutes tes clientes</p>
          <h1 style={titleStyle}>Le flux du jour</h1>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {pushState !== "hidden" && (
            <button
              type="button"
              style={{
                ...ghostBtnStyle,
                ...(pushState === "on"
                  ? { borderColor: "rgba(130,195,158,.5)", color: "#82c39e" }
                  : null),
                opacity: pushState === "denied" ? 0.55 : 1,
              }}
              disabled={pushState === "busy" || pushState === "denied"}
              title={
                pushState === "denied"
                  ? "Notifications refusées dans les réglages du navigateur pour ce site"
                  : pushState === "on"
                    ? "Cet appareil est notifié quand une cliente écrit ou partage un ressenti — toucher pour couper"
                    : "Être notifiée sur cet appareil quand une cliente écrit ou partage un ressenti"
              }
              onClick={handlePushToggle}
            >
              {pushState === "busy"
                ? "…"
                : pushState === "on"
                  ? "Notifications actives"
                  : pushState === "denied"
                    ? "Notifications refusées"
                    : "Activer les notifications"}
            </button>
          )}
          <button type="button" style={ghostBtnStyle} onClick={load} disabled={loading}>
            {loading ? "Chargement…" : "Actualiser"}
          </button>
        </div>
      </header>

      {error && (
        <div style={errorStyle}>
          Impossible de charger le flux : {error}
          <button type="button" style={{ ...linkBtnStyle, marginLeft: 8 }} onClick={load}>Réessayer</button>
        </div>
      )}

      {loading && !error && (
        <div aria-hidden>
          {[0.9, 0.6, 0.35].map((o) => (
            <div key={o} style={{ ...skeletonStyle, opacity: o }} />
          ))}
        </div>
      )}

      {!loading && !error && (
        <>
          {/* Les entrants restent en tête : c'est la seule inbox opérationnelle. */}
          <section aria-labelledby="suivi-a-repondre">
            <h2 id="suivi-a-repondre" style={sectionTitleStyle}>
              À répondre {toReply.length > 0 ? `(${toReply.length})` : ""}
            </h2>
            {toReply.map((item) => renderItem(item, { isTreated: false }))}
          </section>

          <section aria-labelledby="suivi-informations" style={{ marginTop: toReply.length > 0 ? 22 : 0 }}>
            <h2 id="suivi-informations" style={sectionTitleStyle}>
              Informations {information.length > 0 ? `(${information.length})` : ""}
            </h2>
            {information.map((item) => renderItem(item, { isTreated: false }))}
          </section>

          {toReply.length === 0 && information.length === 0 && (
            <div style={emptyStyle}>
              <p style={emptyTitleStyle}>Tout est à jour.</p>
              <p style={emptyTextStyle}>
                Aucun message, ressenti ou photo en attente. Les nouveaux apparaîtront ici.
              </p>
            </div>
          )}

          {/* Traités : historique secondaire, replié par défaut. */}
          {treated.length > 0 && (
            <section style={{ marginTop: 22 }}>
              <button type="button" style={foldBtnStyle} onClick={() => setShowTreated((v) => !v)}>
                {showTreated ? "▾" : "▸"} Traités ({treated.length})
              </button>
              {showTreated && treated.map((item) => renderItem(item, { isTreated: true }))}
            </section>
          )}

          {/* Repères secondaires, toujours après les entrants. */}
          {(upcomingRdv.length > 0 || packAlerts.length > 0 || silences.length > 0) && (
            <section style={{ marginTop: 28 }}>
              <h2 style={sectionTitleStyle}>Repères de suivi</h2>
              {upcomingRdv.map((c) => {
                const local = findLocal(c);
                return (
                  <div key={`rdv-${c.client_id}`} style={rdvCardStyle}>
                    <div style={{ minWidth: 0 }}>
                      <button type="button" style={cardNameBtnStyle} title="Courbes et échéances" onClick={() => setPanelFor(c.client_id)}>{c.first_name || "Cliente"}</button>
                      <span style={rdvTextStyle}> · Rendez-vous de suivi · {formatRdv(c.rdv_scheduled_at)}</span>
                    </div>
                    {local && onOpenClient && (
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button
                          type="button"
                          style={seenBtnStyle}
                          onClick={() => {
                            setSuiviIntent(local.id, "briefing");
                            onOpenClient(local.id);
                          }}
                        >
                          Préparer le rendez-vous
                        </button>
                        <button
                          type="button"
                          style={ghostBtnStyle}
                          onClick={() => onOpenClient(local.id)}
                        >
                          Faire le suivi →
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
              {packAlerts.map((a) => (
                <div key={`pack-${a.clientId}-${a.label}`} style={rdvCardStyle}>
                  <div style={{ minWidth: 0 }}>
                    <span style={rdvTextStyle}>{a.label}</span>
                  </div>
                  {onOpenClient && (
                    <button type="button" style={linkBtnStyle} onClick={() => onOpenClient(a.clientId)}>
                      Ouvrir la fiche →
                    </button>
                  )}
                </div>
              ))}
              {silences.map(({ client, daysSilent }) => {
                const local = findLocal(client);
                return (
                  <div key={client.client_id} style={silenceCardStyle}>
                    <div style={{ minWidth: 0 }}>
                      <button type="button" style={cardNameBtnStyle} title="Courbes et échéances" onClick={() => setPanelFor(client.client_id)}>{client.first_name || "Cliente"}</button>
                      <span style={silenceTextStyle}>
                        {daysSilent === null
                          ? ` · aucun ressenti depuis au moins ${STREAM_DAYS} jours`
                          : ` · aucun ressenti depuis ${daysSilent} jours`}
                      </span>
                    </div>
                    {local && onOpenClient && (
                      <button type="button" style={linkBtnStyle} onClick={() => onOpenClient(local.id)}>
                        Ouvrir la fiche →
                      </button>
                    )}
                  </div>
                );
              })}
            </section>
          )}

        </>
      )}

      {/* V2 (2026-07-22) — Panneau latéral par cliente : courbes 30 j,
          échéances et dernière note, sans quitter le flux. Clic sur le
          prénom pour ouvrir, Échap / fond / croix pour fermer. */}
      {panelFor && (() => {
        const client = clientById.get(panelFor);
        if (!client) return null;
        const local = findLocal(client);
        const clientFbs = feedbacksByClient.get(client.client_id) || [];
        const myPackAlerts = local ? packAlerts.filter((a) => a.clientId === local.id) : [];
        const lastNote = clientFbs.find((f) => f.note);
        const lastFb = clientFbs[0];
        const silentDays = lastFb ? daysSince(lastFb.created_at) : null;
        return (
          <div style={panelBackdropStyle} onClick={() => setPanelFor(null)}>
            {/* .suivi-panel : plein écran sur téléphone (Suivi mobile 2026-07-26,
                média query dans index.css) */}
            <aside
              className="suivi-panel"
              style={panelStyle}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-label={`Suivi de ${client.first_name || "la cliente"}`}
            >
              <header style={panelHeaderStyle}>
                <h2 style={panelNameStyle}>{client.first_name || "Cliente"}</h2>
                <button type="button" style={panelCloseStyle} onClick={() => setPanelFor(null)} aria-label="Fermer (Échap)">
                  ✕
                </button>
              </header>

              <h3 style={panelSectionTitleStyle}>Échéances</h3>
              {/* Audit 2026-07-25 — uniquement un RDV FUTUR : l'ancien RDV
                  d'anamnèse passé s'affichait comme « Prochain RDV ». */}
              <div style={panelRowStyle}>
                {client.rdv_scheduled_at &&
                new Date(client.rdv_scheduled_at).getTime() > Date.now()
                  ? <>Prochain RDV · <strong style={{ color: "#f0efe6" }}>{formatWhen(client.rdv_scheduled_at)}</strong></>
                  : "Aucun rendez-vous à venir posé"}
              </div>
              {myPackAlerts.map((a) => (
                <div key={`pa-${a.label}`} style={panelRowStyle}>{a.label}</div>
              ))}
              <div style={panelRowStyle}>
                {lastFb
                  ? silentDays === 0
                    ? "Ressenti reçu aujourd'hui"
                    : `Dernier ressenti il y a ${silentDays} jour${silentDays > 1 ? "s" : ""}`
                  : `Aucun ressenti sur ${STREAM_DAYS} jours`}
              </div>

              <h3 style={panelSectionTitleStyle}>Courbes · {STREAM_DAYS} jours</h3>
              {clientFbs.length >= 3 ? (
                <div style={panelChartWrapStyle}>
                  <FeedbacksTrendChart feedbacks={clientFbs} />
                </div>
              ) : (
                <p style={panelMutedStyle}>
                  Pas encore assez de ressentis pour tracer une courbe (3 minimum).
                </p>
              )}

              {/* Ordre validé Benoit : ressentis (courbes + dernière note)
                  AVANT les repas, puis l'hydratation. */}
              {lastNote && (
                <>
                  <h3 style={panelSectionTitleStyle}>Dernière note</h3>
                  <p style={{ ...noteStyle, marginTop: 0 }}>« {lastNote.note} »</p>
                  <p style={panelMutedStyle}>{formatWhen(lastNote.created_at)}</p>
                </>
              )}

              {/* Boucle de suivi (cadrage v2) — Repas · 7 jours. Synthèse
                  factuelle : « comme prévu », X sur Y renseignés avec les
                  APPELLATIONS DU PROGRAMME, « à venir » pour les repas
                  futurs d'aujourd'hui. JAMAIS de score. */}
              <h3 style={panelSectionTitleStyle}>Repas · 7 jours</h3>
              {!mealSummary || mealSummary.status === "busy" ? (
                <p style={panelMutedStyle}>Chargement…</p>
              ) : mealSummary.status !== "done" || !mealSummary.meals ? (
                <p style={panelMutedStyle}>Pas de suivi de repas sur cette période.</p>
              ) : (
                (() => {
                  const t = mealSummary.meals.totals;
                  const visibleDays = mealSummary.meals.days.filter(
                    (d) => d.filled > 0 || (d.is_today && d.entries.length > 0),
                  );
                  return (
                    <>
                      <div style={panelRowStyle}>
                        <strong style={{ color: "#f0efe6" }}>{t.filled}</strong> repas renseigné{t.filled > 1 ? "s" : ""}
                      </div>
                      <div style={panelRowStyle}>
                        <strong style={{ color: "#82c39e" }}>{t.done}</strong> comme prévu ·{" "}
                        <strong style={{ color: "#d9b56a" }}>{t.replaced}</strong> remplacé{t.replaced > 1 ? "s" : ""} ·{" "}
                        <strong style={{ color: "#c9c9bd" }}>{t.skip}</strong> sauté{t.skip > 1 ? "s" : ""}
                      </div>
                      {t.days_without_info > 0 && (
                        <div style={panelMutedStyle}>
                          {t.days_without_info} jour{t.days_without_info > 1 ? "s" : ""} sans saisie
                        </div>
                      )}
                      {visibleDays.map((d) => (
                        <details key={d.day} style={{ margin: "6px 0 0" }} open={d.is_today}>
                          <summary style={{ ...panelRowStyle, cursor: "pointer", listStyle: "none" }}>
                            {formatDayShort(d.day)} ·{" "}
                            {d.planned !== null
                              ? `${d.filled} repas sur ${d.planned} renseignés`
                              : `${d.filled} repas renseigné${d.filled > 1 ? "s" : ""}`}
                          </summary>
                          {d.entries.map((e, i) => (
                            <div key={i} style={{ ...panelMutedStyle, margin: "4px 0 0 12px" }}>
                              {e.slot_label || mealSlotLabel(e.slot)} · {mealStateLabel(e.status)}
                              {e.replaced_choice ? ` → ${e.replaced_choice}` : ""}
                              {!e.slot_label && e.meal_title ? ` (${e.meal_title})` : ""}
                            </div>
                          ))}
                        </details>
                      ))}
                    </>
                  );
                })()
              )}

              {/* Hydratation · 7 jours — absente si le repère est à 0
                  (Anissa ne l'utilise pas → la donnée reste locale à l'app,
                  l'endpoint ne lit même pas la table). */}
              {mealSummary?.status === "done" && mealSummary.water && (
                <>
                  <h3 style={panelSectionTitleStyle}>Hydratation · 7 jours</h3>
                  <div style={panelRowStyle}>
                    <strong style={{ color: "#8fd0f0" }}>{frNumber(mealSummary.water.average)}</strong>{" "}
                    verres/jour
                  </div>
                  <div style={panelMutedStyle}>
                    {mealSummary.water.filled_days} jour{mealSummary.water.filled_days > 1 ? "s" : ""} renseigné
                    {mealSummary.water.filled_days > 1 ? "s" : ""} sur {mealSummary.water.window_days} ·
                    repère personnel : {mealSummary.water.target} verres
                  </div>
                  {formatWaterTrend(mealSummary.water.trend) && (
                    <div style={panelMutedStyle}>{formatWaterTrend(mealSummary.water.trend)}</div>
                  )}
                </>
              )}

              {local && onOpenClient && (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 22 }}>
                  <button
                    type="button"
                    style={seenBtnStyle}
                    onClick={() => {
                      setSuiviIntent(local.id, "briefing");
                      setPanelFor(null);
                      onOpenClient(local.id);
                    }}
                  >
                    Préparer le rendez-vous
                  </button>
                  <button
                    type="button"
                    style={ghostBtnStyle}
                    onClick={() => {
                      setPanelFor(null);
                      onOpenClient(local.id);
                    }}
                  >
                    Ouvrir la fiche →
                  </button>
                </div>
              )}
            </aside>
          </div>
        );
      })()}

      {/* Visionneuse photos — par-dessus le flux : croix, flèches (souris
          et clavier), bande de vignettes pour sauter directement. */}
      {lightbox && (() => {
        const cur = lightbox.photos[lightbox.index];
        const many = lightbox.photos.length > 1;
        const goto = (index) => setLightbox({ ...lightbox, index });
        return (
          <div style={lightboxBackdropStyle} onClick={() => setLightbox(null)} role="dialog" aria-label="Photo de repas">
            <div style={lightboxTopBarStyle} onClick={(e) => e.stopPropagation()}>
              <div style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                <span style={lightboxTitleStyle}>{lightbox.clientName}</span>
                <span style={lightboxMetaStyle}>
                  {" "}· {PHOTO_SLOT_LABELS[cur.slot] || "Repas"} · {formatWhen(cur.created_at)}
                </span>
              </div>
              <button type="button" style={lightboxCloseStyle} onClick={() => setLightbox(null)} aria-label="Fermer (Échap)">
                ✕
              </button>
            </div>

            <div style={lightboxBodyStyle} onClick={(e) => e.stopPropagation()}>
              {many && (
                <button
                  type="button"
                  style={lightboxNavStyle}
                  onClick={() => goto((lightbox.index - 1 + lightbox.photos.length) % lightbox.photos.length)}
                  aria-label="Photo précédente (←)"
                >
                  ‹
                </button>
              )}
              <img key={cur.id} src={cur.url} alt="" style={lightboxImgStyle} />
              {many && (
                <button
                  type="button"
                  style={lightboxNavStyle}
                  onClick={() => goto((lightbox.index + 1) % lightbox.photos.length)}
                  aria-label="Photo suivante (→)"
                >
                  ›
                </button>
              )}
            </div>

            {/* Réactions emoji (façon Benfitcoach) — la cliente les voit dans son app */}
            <div style={lightboxReactRowStyle} onClick={(e) => e.stopPropagation()}>
              {MEAL_REACTIONS.map((emo) => {
                const active = cur.reaction === emo;
                return (
                  <button
                    key={emo}
                    type="button"
                    onClick={() => handleReactPhoto(cur, emo)}
                    style={{ ...lightboxReactBtnStyle, ...(active ? lightboxReactBtnActiveStyle : null) }}
                    aria-label={active ? `Retirer la réaction ${emo}` : `Réagir ${emo}`}
                    aria-pressed={active}
                  >
                    {emo}
                  </button>
                );
              })}
            </div>

            {cur.note && (
              <p style={lightboxNoteStyle} onClick={(e) => e.stopPropagation()}>« {cur.note} »</p>
            )}

            {many && (
              <div style={lightboxStripStyle} onClick={(e) => e.stopPropagation()}>
                {lightbox.photos.map((p, i) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => goto(i)}
                    style={{
                      ...lightboxStripThumbStyle,
                      outline: i === lightbox.index ? "2px solid #fff" : "none",
                      opacity: i === lightbox.index ? 1 : 0.55,
                    }}
                    aria-label={`Photo ${i + 1} sur ${lightbox.photos.length}`}
                  >
                    <img src={p.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

// ─── Styles (palette journey.css : ivoire premium, vert Anissa) ──────────

const wrapStyle = { maxWidth: 720, margin: "0 auto", padding: "8px 4px 60px" };

const pageHeaderStyle = {
  display: "flex", alignItems: "flex-end", justifyContent: "space-between",
  gap: 12, marginBottom: 18,
};

const eyebrowStyle = {
  margin: 0, fontSize: ".68rem", fontWeight: 600, letterSpacing: ".14em",
  textTransform: "uppercase", color: "#c9a96a",
};

const titleStyle = {
  margin: "4px 0 0", fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: "1.7rem", color: "#f0efe6",
};

const sectionTitleStyle = {
  margin: "0 0 10px", fontSize: ".72rem", fontWeight: 700,
  letterSpacing: ".12em", textTransform: "uppercase", color: "rgba(245,242,233,.55)",
};

// 2026-07-21 : passage au thème vert sombre du cockpit — le composant était
// écrit avec l'encre/papier de la charte fiche claire (jrn) et devenait
// illisible sur le fond vert profond.
const cardStyle = {
  background: "#20362a",
  border: "1px solid rgba(255,255,255,.08)",
  borderRadius: 12,
  boxShadow: "0 1px 3px rgba(0,0,0,.3)",
  padding: "14px 16px",
  marginBottom: 12,
};

const cardHeaderStyle = { display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" };
const cardNameRowStyle = { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" };
const cardNameStyle = { fontWeight: 700, color: "#f0efe6", fontSize: ".98rem" };
// V2 (2026-07-22) : le prénom est cliquable → panneau latéral de la cliente.
const cardNameBtnStyle = {
  ...cardNameStyle,
  border: "none", background: "transparent", padding: 0, cursor: "pointer",
  fontFamily: "inherit", textDecoration: "underline dotted rgba(255,255,255,.35)",
  textUnderlineOffset: 3,
};

// Panneau latéral V2 — tiroir droit sombre, au-dessus du flux.
const panelBackdropStyle = {
  position: "fixed", inset: 0, zIndex: 1500,
  background: "rgba(10,16,11,.45)",
};
const panelStyle = {
  position: "fixed", top: 0, right: 0, bottom: 0, zIndex: 1501,
  width: "min(420px, 92vw)", boxSizing: "border-box",
  background: "#18291e",
  borderLeft: "1px solid rgba(255,255,255,.1)",
  boxShadow: "-18px 0 50px rgba(0,0,0,.45)",
  padding: "18px 20px 28px", overflowY: "auto",
};
const panelHeaderStyle = {
  display: "flex", alignItems: "center", justifyContent: "space-between",
  gap: 10, marginBottom: 6,
};
const panelNameStyle = {
  fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: "1.35rem", color: "#f0efe6", margin: 0,
};
const panelCloseStyle = {
  border: "none", background: "transparent", color: "rgba(255,255,255,.7)",
  fontSize: "1.05rem", cursor: "pointer", padding: 4,
};
const panelSectionTitleStyle = {
  margin: "18px 0 8px", fontSize: ".68rem", fontWeight: 700,
  letterSpacing: ".12em", textTransform: "uppercase", color: "rgba(245,242,233,.5)",
};
const panelRowStyle = {
  fontSize: ".85rem", color: "#d8d6c6", padding: "7px 0",
  borderBottom: "1px solid rgba(255,255,255,.06)",
};
const panelMutedStyle = { fontSize: ".78rem", color: "rgba(255,255,255,.62)", margin: "6px 0 0" };
const panelChartWrapStyle = { borderRadius: 12, overflow: "hidden" };
const cardWhenStyle = { fontSize: ".78rem", color: "rgba(255,255,255,.45)", marginTop: 2 };

const badgeStyle = {
  fontSize: ".68rem", fontWeight: 600, padding: "2px 9px", borderRadius: 999,
};

const newBadgeStyle = {
  ...badgeStyle,
  color: "#f5e2b9",
  background: "rgba(201,169,106,.22)",
  border: "1px solid rgba(201,169,106,.34)",
};

const chipsRowStyle = { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 };
const chipStyle = { fontSize: ".74rem", fontWeight: 500, padding: "3px 9px", borderRadius: 999 };

const noteStyle = {
  margin: "10px 0 0", fontFamily: "'Playfair Display', Georgia, serif",
  fontStyle: "italic", fontSize: ".95rem", lineHeight: 1.5, color: "#d8d6c6",
};

const actionsRowStyle = { display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" };

const seenBtnStyle = {
  border: "none", borderRadius: 9, padding: "7px 16px", minHeight: 44, cursor: "pointer",
  background: "linear-gradient(135deg, #4a7a5c, #2E5B40)", color: "#FAF7EF",
  fontSize: ".82rem", fontWeight: 600,
};

const ghostBtnStyle = {
  border: "1px solid rgba(255,255,255,.2)", borderRadius: 9, padding: "7px 14px",
  minHeight: 44, cursor: "pointer", background: "transparent", color: "#cfe0d2",
  fontSize: ".82rem", fontWeight: 500,
};

const linkBtnStyle = {
  border: "none", background: "transparent", cursor: "pointer", padding: 0,
  color: "#c9a96a", fontSize: ".78rem", fontWeight: 600,
  whiteSpace: "nowrap",
};

const replyBoxStyle = { marginTop: 10, display: "flex", flexDirection: "column", gap: 8 };
const replyTextareaStyle = {
  width: "100%", boxSizing: "border-box", borderRadius: 10, padding: "10px 12px",
  border: "1px solid rgba(255,255,255,.16)", fontSize: ".88rem", fontFamily: "inherit",
  resize: "vertical", background: "rgba(15,26,19,.55)", color: "#f0efe6",
};
const replyStatusStyle = { margin: "8px 0 0", fontSize: ".8rem", fontWeight: 600 };

const rdvCardStyle = {
  display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
  flexWrap: "wrap",
  background: "#20362a",
  border: "1px solid rgba(255,255,255,.08)",
  borderLeft: "3px solid #6abf8a",
  borderRadius: 12, padding: "11px 16px", marginBottom: 8,
};
const rdvTextStyle = { fontSize: ".85rem", color: "#d8d6c6" };

const photoRowStyle = { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 };
const photoThumbWrapStyle = {
  display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
  textDecoration: "none",
};
const photoThumbStyle = {
  width: 72, height: 72, objectFit: "cover", borderRadius: 10,
  border: "1px solid rgba(255,255,255,.15)", display: "block",
};
const photoThumbLabelStyle = { fontSize: ".64rem", fontWeight: 600, color: "rgba(255,255,255,.45)" };
// Badge réaction en coin de vignette : ce qu'Anissa a déjà « liké ».
const photoThumbReactBadgeStyle = {
  position: "absolute", bottom: -4, right: -4,
  width: 22, height: 22, borderRadius: "50%",
  display: "flex", alignItems: "center", justifyContent: "center",
  fontSize: ".8rem", background: "#20362a",
  border: "1px solid rgba(255,255,255,.18)", boxShadow: "0 1px 4px rgba(0,0,0,.4)",
};

// Visionneuse photos (lightbox) — sombre, plein écran, au-dessus de tout.
const lightboxBackdropStyle = {
  position: "fixed", inset: 0, zIndex: 2000,
  background: "rgba(12,16,12,0.92)",
  display: "flex", flexDirection: "column",
  padding: "14px 18px 18px",
};
const lightboxTopBarStyle = {
  display: "flex", alignItems: "center", justifyContent: "space-between",
  gap: 12, color: "#fff", marginBottom: 10,
};
const lightboxTitleStyle = { fontWeight: 700, fontSize: "1rem" };
const lightboxMetaStyle = { fontSize: ".85rem", color: "rgba(255,255,255,.65)" };
const lightboxCloseStyle = {
  border: "1px solid rgba(255,255,255,.25)", background: "rgba(255,255,255,.08)",
  color: "#fff", borderRadius: 999, width: 38, height: 38, cursor: "pointer",
  fontSize: "1rem", flexShrink: 0,
};
const lightboxBodyStyle = {
  flex: 1, minHeight: 0, display: "flex", alignItems: "center",
  justifyContent: "center", gap: 14,
};
const lightboxImgStyle = {
  maxHeight: "100%", maxWidth: "calc(100% - 130px)",
  objectFit: "contain", borderRadius: 14,
};
const lightboxNavStyle = {
  border: "1px solid rgba(255,255,255,.25)", background: "rgba(255,255,255,.08)",
  color: "#fff", borderRadius: 999, width: 44, height: 44, cursor: "pointer",
  fontSize: "1.5rem", lineHeight: 1, flexShrink: 0,
};
const lightboxNoteStyle = {
  margin: "12px auto 0", maxWidth: 640, textAlign: "center",
  fontFamily: "'Playfair Display', Georgia, serif", fontStyle: "italic",
  fontSize: "1rem", color: "rgba(255,255,255,.85)",
};
// Rangée de réactions dans la visionneuse — emojis cliquables, l'actif ressort.
const lightboxReactRowStyle = {
  display: "flex", justifyContent: "center", gap: 6, marginTop: 14, flexWrap: "wrap",
};
const lightboxReactBtnStyle = {
  fontSize: "1.25rem", lineHeight: 1, padding: "6px 10px", borderRadius: 999,
  cursor: "pointer", background: "rgba(255,255,255,.08)",
  border: "1px solid rgba(255,255,255,.14)", transition: "transform .1s, background .15s",
};
const lightboxReactBtnActiveStyle = {
  background: "rgba(201,169,106,.28)", border: "1px solid rgba(201,169,106,.7)",
  transform: "scale(1.12)",
};

const lightboxStripStyle = {
  display: "flex", justifyContent: "center", gap: 8, marginTop: 12, flexWrap: "wrap",
};
const lightboxStripThumbStyle = {
  width: 52, height: 52, borderRadius: 10, overflow: "hidden",
  border: "none", padding: 0, cursor: "pointer", background: "transparent",
};

// Lecture IA privée (mode démo) — encadré pointillé doré : clairement
// distinct d'un message, marqué « visible par toi seule ».
const aiLectureBoxStyle = {
  marginTop: 10, padding: "10px 14px", borderRadius: 10,
  background: "rgba(201,169,106,.07)", border: "1px dashed rgba(201,169,106,.35)",
};
const aiLectureTitleStyle = {
  fontSize: ".66rem", fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase",
  color: "#c9a96a", marginBottom: 6,
};
const aiLectureTextStyle = { margin: 0, fontSize: ".85rem", lineHeight: 1.55, color: "#d8d6c6" };

const silenceCardStyle = {
  display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
  background: "rgba(201,169,106,0.10)",
  border: "1px solid rgba(184,134,42,0.25)",
  borderRadius: 12, padding: "11px 16px", marginBottom: 8,
};
const silenceTextStyle = { fontSize: ".85rem", color: "#e0c890" };

const emptyStyle = {
  background: "rgba(130,195,158,0.10)",
  border: "1px solid rgba(130,195,158,0.30)",
  borderRadius: 12, padding: "26px 20px", textAlign: "center",
};
const emptyTitleStyle = {
  margin: 0, fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: "1.15rem", color: "#dff0e4",
};
const emptyTextStyle = { margin: "6px 0 0", fontSize: ".84rem", color: "rgba(245,242,233,.55)" };

const foldBtnStyle = {
  border: "none", background: "transparent", cursor: "pointer", padding: "4px 0",
  fontSize: ".8rem", fontWeight: 600, color: "rgba(255,255,255,.45)", marginBottom: 8,
};

const skeletonStyle = {
  height: 96, borderRadius: 12, background: "rgba(255,255,255,.06)", marginBottom: 12,
};

const errorStyle = {
  background: "rgba(232,144,144,0.12)", border: "1px solid rgba(232,144,144,0.35)",
  borderRadius: 12, padding: "12px 16px", fontSize: ".85rem", color: "#e89090",
  marginBottom: 14,
};
